import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { selectUser } from '../features/currentUser/currentUserSelectors';
import { logout, refreshToken } from '../features/currentUser/currentUserSlice';
import { refreshWithToken } from '../apis/auth';
import { isTokenExpired } from '../utils/tokenUtils';

type ValidationState = 'validating' | 'valid' | 'invalid';

/**
 * Hook to validate token on mount, focus, and visibility change
 * @param isProtected Whether the current route requires authentication
 */
export const useTokenValidation = (isProtected: boolean): ValidationState => {
  const { accessToken, refreshToken: storedRefreshToken } = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [state, setState] = useState<ValidationState>('validating');
  const isValidatingRef = useRef(false);

  const validateToken = useCallback(async () => {
    // Prevent parallel validation requests
    if (isValidatingRef.current) return;

    if (!isProtected) {
      setState('valid');
      return;
    }

    if (!accessToken || !storedRefreshToken) {
      setState('invalid');
      return;
    }

    const expired = isTokenExpired(accessToken);

    if (expired === false) {
      setState('valid');
      return;
    }

    // Token is expired or can't determine (null) - try refresh
    isValidatingRef.current = true;
    try {
      const newToken = await refreshWithToken(storedRefreshToken);
      dispatch(refreshToken(newToken));
      setState('valid');
    } catch {
      dispatch(logout());
      setState('invalid');
    } finally {
      isValidatingRef.current = false;
    }
  }, [accessToken, storedRefreshToken, isProtected, dispatch]);

  // Validate on mount and when tokens change
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Validate on window focus and visibility change
  useEffect(() => {
    if (!isProtected) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateToken();
      }
    };

    const handleFocus = () => {
      validateToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isProtected, validateToken]);

  return state;
};
